import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Swords, BookOpen, Shield, Scale } from "lucide-react";

// Footer rendered on all dashboard / auth-y pages. Hidden on active
// game and spectator pages where vertical space is at a premium and
// a footer would push the hand row off-screen.
//
// Routes that suppress the footer:
//   /game/:id, /spectate/:id, /auth (callback)
const SUPPRESS_PREFIXES = ["/game/", "/spectate/"];

export default function Footer() {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  if (SUPPRESS_PREFIXES.some((p) => pathname.startsWith(p))) {
    return null;
  }

  const year = new Date().getFullYear();

  return (
    <footer
      className="mt-auto border-t border-[#1A237E]/10 dark:border-[#E3F2FD]/10 bg-white/40 dark:bg-slate-900/40 backdrop-blur"
      data-testid="site-footer"
    >
      <div className="max-w-5xl mx-auto px-4 py-5 flex flex-col items-center gap-3 text-xs text-[#1A237E]/70 dark:text-[#E3F2FD]/70">
        <div className="flex items-center gap-2">
          <Swords className="w-4 h-4 text-[#38B6FF]" />
          <span
            className="font-bold text-[#1A237E] dark:text-[#E3F2FD]"
            style={{ fontFamily: "Fredoka" }}
          >
            {t("common.appName")}
          </span>
          <span className="text-[#1A237E]/40 dark:text-[#E3F2FD]/40">
            · {t("footer.tagline")}
          </span>
        </div>

        <nav
          aria-label={t("footer.navAria")}
          className="flex items-center gap-4 flex-wrap justify-center"
        >
          <Link
            to="/rules"
            className="inline-flex items-center gap-1 hover:text-[#38B6FF] transition-colors"
            data-testid="footer-rules-link"
          >
            <BookOpen className="w-3.5 h-3.5" />
            {t("footer.rules")}
          </Link>
          <Link
            to="/privacy"
            className="inline-flex items-center gap-1 hover:text-[#38B6FF] transition-colors"
            data-testid="footer-privacy-link"
          >
            <Shield className="w-3.5 h-3.5" />
            {t("footer.privacy")}
          </Link>
          <Link
            to="/terms"
            className="inline-flex items-center gap-1 hover:text-[#38B6FF] transition-colors"
            data-testid="footer-terms-link"
          >
            <Scale className="w-3.5 h-3.5" />
            {t("footer.terms")}
          </Link>
        </nav>

        <p className="text-[10px] sm:text-xs text-[#1A237E]/50 dark:text-[#E3F2FD]/50 text-center">
          {t("footer.copyright", { year })}
        </p>
      </div>
    </footer>
  );
}
